export const EPHEMERAL_CREDENTIAL_STORAGE_KEY = 'xapi.ephemeralCredentials.v1';
export const WS_AUTH_FALLBACK_PROTOCOL = 'xapi-ws-v1';
const FULL_SESSION_LOGIN_METHODS = new Set(['twitter', 'github', 'google', 'wallet']);

export const isFullAccountSession = (loginMethod) =>
  FULL_SESSION_LOGIN_METHODS.has(String(loginMethod || ''));

function decodeBase64Url(value) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return atob(padded);
}

export function sessionCredentialIdentity(accessToken) {
  try {
    const payload = JSON.parse(decodeBase64Url(String(accessToken || '').split('.')[1] || ''));
    if (!payload?.userId) return null;
    const loginMethod = String(payload.loginMethod || 'unknown');
    const authSessionVersion = Number.isInteger(payload.authSessionVersion)
      ? payload.authSessionVersion
      : 0;
    return {
      userId: String(payload.userId),
      loginMethod,
      authSessionVersion,
      owner: `${payload.userId}:${authSessionVersion}:${loginMethod}`,
    };
  } catch {
    return null;
  }
}

export function createEphemeralCredentialStore(
  storageProvider = () => globalThis.window?.sessionStorage || globalThis.sessionStorage,
) {
  const storage = () => {
    try { return storageProvider(); } catch { return null; }
  };
  const clear = () => {
    try { storage()?.removeItem(EPHEMERAL_CREDENTIAL_STORAGE_KEY); } catch {}
  };
  const read = (owner) => {
    try {
      const raw = storage()?.getItem(EPHEMERAL_CREDENTIAL_STORAGE_KEY);
      const value = raw ? JSON.parse(raw) : null;
      if (!value || value.version !== 1 || value.owner !== owner) {
        if (raw) clear();
        return { version: 1, owner, domain: null, ws: {} };
      }
      return {
        version: 1,
        owner,
        domain: value.domain || null,
        ws: value.ws && typeof value.ws === 'object' ? value.ws : {},
      };
    } catch {
      clear();
      return { version: 1, owner, domain: null, ws: {} };
    }
  };
  const write = (value) => {
    try { storage()?.setItem(EPHEMERAL_CREDENTIAL_STORAGE_KEY, JSON.stringify(value)); } catch {}
  };
  return {
    clear,
    getDomain(owner) { return read(owner).domain; },
    setDomain(owner, credential) {
      const value = read(owner);
      value.domain = credential;
      write(value);
    },
    getWs(owner, endpointId) { return read(owner).ws[endpointId] || null; },
    setWs(owner, endpointId, credential) {
      const value = read(owner);
      value.ws[endpointId] = credential;
      write(value);
    },
  };
}

export function buildWsCredentialProtocols(token, applicationProtocols = []) {
  const protocols = (Array.isArray(applicationProtocols) ? applicationProtocols : [])
    .filter((protocol) => typeof protocol === 'string' && protocol && !protocol.startsWith('xapi-key.'));
  if (protocols.length === 0) protocols.push(WS_AUTH_FALLBACK_PROTOCOL);
  if (token) protocols.push(`xapi-key.${token}`);
  return protocols;
}
